import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { ErrorBanner, Loading } from "../../../components/Shell";
import type { TopBarSpec } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import { Menu } from "../../../components/ui/Menu";
import { AssessRejected, NOTE_MAX } from "../api/assessApi";
import type { AssessApi } from "../api/assessApi";
import type { AssessStudent, AssessView, Level, ChipTone, Draft } from "../types";
import {
  agsChip,
  byName,
  draftOf,
  isDirty,
  levelChip,
  neighbour,
  todayParts,
  todayText,
} from "../lib/logic";
import "../../../styles/assess.css";

/** AGS 전송 상태를 다시 읽는 간격(대기 → 전송됨 · 실패 · 재시도 중) */
export const ASSESS_POLL_MS = 5000;

const LEVELS: readonly Level[] = [1, 2, 3];
const TONE: Record<ChipTone, string> = {
  ok: "pf-chip-static--ok",
  run: "pf-chip-static--run",
  err: "pf-chip-static--err",
  off: "",
};

/**
 * FS-11-04 관찰 메모·성취 입력(교사 · SU/공통 · SCR v0.6 sfs-11-04) — 콘솔 탭 /teacher/room/:roomId/assess.
 *  - 위 '학생별 입력' 표: 별명 · 오늘 활동(참고용 — 성취에 자동 반영 안 함) · 성취 칩 · 메모 · AGS 칩 · 열기/편집 중.
 *    정렬은 이름순 고정 · 성취 열은 입력값이지 비교 지표가 아니다 · 학생·관찰자 화면에 비노출(SR-ACH-01).
 *  - 아래 편집 칸(굵은 테두리): 관찰 메모(교사 자유 텍스트 ≤2000자) · 3단계 성취(1 참여 · 2 수행 · 3 원리 적용 — 다시 누르면 비움)
 *    · 자동 제안 표시(활동 auto_complete + 모드 완료 → 2 제안, 교사가 저장해야 확정·AGS) · 이전/다음 학생 · 저장·AGS 전송.
 *  - 활동 고르기(기본 = 열린 룸의 활동, 없으면 가장 최근) · 상단 바 칩 '입력 n/m' · 'AGS 전송 n · 대기 n'.
 *  - 편집 초안은 학생별로 남는다(다른 학생으로 옮겨도 잃지 않는다 — 표에 '저장 전' 표시).
 *  - AGS 실패는 FE-401(교사 배너 · 24시간 자동 재시도) — 5초마다 상태를 다시 읽는다.
 */
export function AssessPanel({
  api,
  courseLabel,
  works,
  onTop,
  onError,
  onSaved,
}: {
  api: AssessApi;
  courseLabel: string;
  /** 학생 pid → 지금 작품(콘솔이 이미 읽은 값 — 편집 칸 머리 'v4 오르막 자동차') */
  works: ReadonlyMap<string, { version: number; title: string }>;
  onTop: (spec: TopBarSpec) => void;
  onError: (e: unknown) => void;
  onSaved: (text: string) => void;
}) {
  const t = useT();
  const [activityId, setActivityId] = useState<string | null>(null);
  const [view, setView] = useState<AssessView | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const [noteErr, setNoteErr] = useState(false);
  const inflight = useRef(false);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      setView(await api.view(activityId));
      setLoadErr(null);
    } catch (e) {
      setLoadErr(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api, activityId]);

  useEffect(() => {
    void load();
    const h = window.setInterval(() => void load(), ASSESS_POLL_MS);
    return () => window.clearInterval(h);
  }, [load]);

  const rows = useMemo(() => byName(view?.students ?? []), [view]);
  const cur = rows.find((r) => r.pid === selected) ?? rows[0] ?? null;
  const draft = cur ? (drafts[cur.pid] ?? draftOf(cur)) : null;
  const dirty = cur ? isDirty(cur, drafts[cur.pid]) : false;
  const activity = view?.activity ?? null;
  const linked = !!activity?.agsLinked;

  const counts = view?.counts;
  const topKey = JSON.stringify(counts ?? null) + courseLabel;
  useEffect(() => {
    onTop({
      icon: "📝",
      title: t("assess.topTitle", { course: courseLabel }),
      chips: counts
        ? [
            {
              label: t("assess.chipEntered", { n: counts.entered, total: counts.total }),
              testId: "assess-entered-chip",
            },
            {
              label: t("assess.chipAgs", { sent: counts.agsSent, pending: counts.agsPending }),
              testId: "assess-ags-chip",
            },
          ]
        : [],
    });
    // 칩 숫자가 바뀔 때만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey, onTop, t]);

  const edit = (patch: Partial<Draft>) => {
    if (!cur || !draft) return;
    setNoteErr(false);
    setDrafts((d) => ({ ...d, [cur.pid]: { ...draft, ...patch } }));
  };

  const pickActivity = (id: string) => {
    setActivityId(id);
    setDrafts({});
    setSelected(null);
  };

  const save = async () => {
    if (!cur || !draft || !activity || saving) return;
    if (draft.note.length > NOTE_MAX) {
      setNoteErr(true);
      return;
    }
    setSaving(true);
    try {
      await api.save(cur.pid, { activityId: activity.id, note: draft.note, level: draft.level });
      setDrafts((d) => {
        const next = { ...d };
        delete next[cur.pid];
        return next;
      });
      onSaved(
        t(linked && draft.level !== null ? "assess.savedAgs" : "assess.saved", {
          name: cur.nickname,
        }),
      );
      await load();
    } catch (e) {
      if (e instanceof AssessRejected && e.field === "note") setNoteErr(true);
      onError(e);
    } finally {
      setSaving(false);
    }
  };

  if (!view)
    return loadErr ? (
      <ErrorBanner
        cause={t("assess.loadFailed")}
        action={t("teacher.common.retryLater")}
        code={loadErr}
      />
    ) : (
      <Loading />
    );

  return (
    <>
      {loadErr && (
        <ErrorBanner
          cause={t("assess.loadFailed")}
          action={t("teacher.common.retryLater")}
          code={loadErr}
        />
      )}
      <section className="pf-pane pf-as__list" aria-labelledby="as-title" data-testid="fs-11-04">
        <div className="pf-row">
          <h2 id="as-title" className="pf-h3">
            {t("assess.title")}
          </h2>
          {view.activities.length > 0 && (
            <div className="pf-row__end pf-as__activity">
              <span className="pf-rc__label" aria-hidden="true">
                {t("assess.activity")}
              </span>
              <Menu
                testId="assess-activity"
                ariaLabel={t("assess.activity")}
                disabled={saving}
                label={<>{activity?.title ?? "—"} ▾</>}
                items={view.activities.map((a) => ({
                  id: a.id,
                  on: a.id === activity?.id,
                  label: a.title,
                }))}
                onPick={pickActivity}
              />
            </div>
          )}
        </div>
        {!activity ? (
          <p className="pf-muted" data-testid="assess-no-activity">
            {t("assess.noActivity")}
          </p>
        ) : rows.length === 0 ? (
          <p className="pf-muted">{t("assess.noStudents")}</p>
        ) : (
          <div className="pf-tbl-scroll pf-as__scroll" tabIndex={0} aria-labelledby="as-title">
            <table className="pf-tbl pf-as__table" data-testid="assess-table">
              <thead>
                <tr>
                  <th scope="col" aria-sort="ascending">
                    {t("assess.col.name")}
                  </th>
                  <th scope="col">{t("assess.col.today")}</th>
                  <th scope="col">{t("assess.col.level")}</th>
                  <th scope="col">{t("assess.col.note")}</th>
                  <th scope="col">{t("assess.col.ags")}</th>
                  <th scope="col">
                    <span className="pf-sr-only">{t("assess.col.open")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const on = s.pid === cur?.pid;
                  const lv = levelChip(t, s);
                  const ags = agsChip(t, s.ags, s.suggested);
                  const unsaved = isDirty(s, drafts[s.pid]);
                  return (
                    <tr
                      key={s.pid}
                      data-pid={s.pid}
                      data-ags={s.ags}
                      className={on ? "pf-tbl__row--on" : undefined}
                      aria-current={on ? "true" : undefined}
                    >
                      <th scope="row">{s.nickname}</th>
                      <td className="pf-muted">{todayText(t, s.today)}</td>
                      <td>
                        <span
                          className={`pf-chip-static ${TONE[lv.tone]}`}
                          data-testid="level-chip"
                        >
                          {lv.text}
                        </span>
                      </td>
                      <td className="pf-muted pf-as__note-cell">
                        <span className="pf-as__clip">{s.note || "—"}</span>
                        {unsaved && (
                          <span
                            className="pf-chip-static pf-chip-static--dash"
                            data-testid="unsaved"
                          >
                            {t("assess.unsaved")}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`pf-chip-static ${TONE[ags.tone]}`} data-testid="ags-chip">
                          {ags.text}
                        </span>
                      </td>
                      <td className="pf-tbl__act">
                        <Button
                          variant="ghost"
                          className="pf-btn--sm pf-as__open"
                          aria-pressed={on}
                          aria-label={t("assess.openOf", { name: s.nickname })}
                          data-testid="assess-open"
                          onClick={() => setSelected(s.pid)}
                        >
                          {t(on ? "assess.editing" : "assess.open")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="pf-muted" data-testid="assess-sort-note">
          {t("assess.sortNote")}
        </p>
      </section>
      {activity && cur && draft && (
        <Editor
          key={cur.pid}
          student={cur}
          draft={draft}
          work={works.get(cur.pid) ?? null}
          linked={linked}
          dirty={dirty}
          saving={saving}
          noteErr={noteErr}
          prev={neighbour(rows, cur.pid, -1)}
          next={neighbour(rows, cur.pid, 1)}
          onEdit={edit}
          onGo={setSelected}
          onSave={() => void save()}
        />
      )}
    </>
  );
}

/** 아래 편집 칸(와이어 '.pane.hero') */
function Editor({
  student: s,
  draft,
  work,
  linked,
  dirty,
  saving,
  noteErr,
  prev,
  next,
  onEdit,
  onGo,
  onSave,
}: {
  student: AssessStudent;
  draft: Draft;
  work: { version: number; title: string } | null;
  linked: boolean;
  dirty: boolean;
  saving: boolean;
  noteErr: boolean;
  prev: string | null;
  next: string | null;
  onEdit: (patch: Partial<Draft>) => void;
  onGo: (pid: string) => void;
  onSave: () => void;
}) {
  const t = useT();
  const noteId = useId();
  const levelId = useId();
  const countId = useId();
  const today = todayParts(t, s.today);
  const sub = [
    work ? t("assess.work", { title: work.title, v: work.version }).trim() : null,
    t("assess.todayLabel", { today: today.join(" · ") || "—" }),
  ]
    .filter(Boolean)
    .join(" · ");
  const failed = s.ags === "failed" || s.ags === "retrying";
  const sendsAgs = linked && draft.level !== null;
  const josa = (n: number) => (n === 3 ? "으로" : "로");

  return (
    <section
      className="pf-pane pf-pane--hero pf-as__editor"
      aria-labelledby="as-editor-name"
      data-testid="assess-editor"
      data-pid={s.pid}
    >
      <div className="pf-row pf-as__head">
        <h2 id="as-editor-name" className="pf-h3">
          {s.nickname}
        </h2>
        <span className="pf-muted">{sub}</span>
      </div>
      {failed && <FeBanner code="FE-401" audience="teacher" />}
      <div className="pf-as__grid">
        <div className="pf-as__memo">
          <label htmlFor={noteId} className="pf-muted">
            {t("assess.noteLabel")}
          </label>
          <textarea
            id={noteId}
            className="pf-input pf-as__textarea"
            value={draft.note}
            maxLength={NOTE_MAX}
            rows={3}
            aria-describedby={countId}
            aria-invalid={noteErr || undefined}
            data-testid="assess-note"
            onChange={(e) => onEdit({ note: e.target.value })}
          />
          <span id={countId} className="pf-muted pf-as__count" data-testid="assess-count">
            {t("assess.noteCount", { n: draft.note.length, max: NOTE_MAX })}
          </span>
          {noteErr && (
            <span className="pf-field-err" role="alert">
              {t("assess.noteTooLong", { max: NOTE_MAX })}
            </span>
          )}
        </div>
        <div className="pf-as__levels">
          <span id={levelId} className="pf-muted">
            {t("assess.levelLabel")}
          </span>
          <div
            className="pf-row"
            role="group"
            aria-labelledby={levelId}
            data-testid="assess-levels"
          >
            {LEVELS.map((n) => {
              const on = draft.level === n;
              const sugg = s.suggested === n && draft.level === null;
              return (
                <Button
                  key={n}
                  variant={on ? "green" : "ghost"}
                  className={`pf-as__lvl${sugg ? " pf-as__lvl--sugg" : ""}`}
                  aria-pressed={on}
                  data-level={n}
                  title={on ? t("assess.levelClear") : undefined}
                  onClick={() => onEdit({ level: on ? null : n })}
                >
                  <b>{n}</b> <span className="pf-as__lvl-name">{t(`assess.level.${n}`)}</span>
                  {sugg && <span className="pf-sr-only"> {t("assess.suggestedMark")}</span>}
                </Button>
              );
            })}
          </div>
          {s.suggested !== null && (
            <p
              className={`pf-banner ${s.level !== null ? "pf-banner--ok" : "pf-banner--warn"} pf-as__sugg`}
              data-testid="assess-suggest"
            >
              {s.level !== null
                ? t("assess.suggestConfirmed", {
                    s: s.suggested,
                    n: s.level,
                    josa: josa(s.level),
                  })
                : t("assess.suggestPending", { s: s.suggested })}
            </p>
          )}
        </div>
      </div>
      <div className="pf-row pf-as__actions">
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={!prev}
          data-testid="assess-prev"
          onClick={() => prev && onGo(prev)}
        >
          {t("assess.prev")}
        </Button>
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={!next}
          data-testid="assess-next"
          onClick={() => next && onGo(next)}
        >
          {t("assess.next")}
        </Button>
        <span className="pf-muted pf-row__end" data-testid="assess-ags-note">
          {t(linked ? "assess.agsNote" : "assess.agsUnlinked")}
        </span>
        <Button
          variant="accent"
          className="pf-btn--sm"
          disabled={!dirty || saving}
          data-testid="assess-save"
          onClick={onSave}
        >
          {t(sendsAgs ? "assess.saveAgs" : "assess.save")}
        </Button>
      </div>
    </section>
  );
}
